import { CONSTANTS } from '../game.js';

export class ScoreManager {
    constructor(scene) {
        this.scene = scene;
        this.score = 0;
        this.highScore = 0;
        this.rubberBand = null;
        
        // 評価カウントの初期化
        this.evaluationCounts = {
            FAIL: 0,
            PASS: 0,
            GOOD: 0,
            GREAT: 0,
            PERFECT: 0
        };
        this.perfectCount = 0; // 完璧評価の獲得回数
        
        // スコア表示の2列構造
        // 上段: スコアラベル（小さめ）
        this.scoreLabelText = scene.add.text(20, 10, 'SCORE', { 
            fontFamily: 'DotGothic16',
            fontSize: '20px', 
            fill: '#fff',
            stroke: '#000',
            strokeThickness: 3,
            padding: { x: 4, y: 2 }
        });
        this.scoreLabelText.setDepth(20);
        
        // 下段: スコア値（大きめ）
        this.scoreValueText = scene.add.text(20, this.scoreLabelText.y + this.scoreLabelText.height - 5, '0', { 
            fontFamily: 'DotGothic16',
            fontSize: '36px', 
            fill: '#fff',
            stroke: '#000',
            strokeThickness: 4,
            padding: { x: 8, y: 4 }
        });
        this.scoreValueText.setDepth(20); // コンボ表示より前面に表示
        
        // ハイスコアの読み込み（ローカルストレージから）
        this.loadHighScore();
    }
    
    /**
     * 必要なコンポーネントを設定
     * @param {RubberBand} rubberBand - ゴム紐
     */
    setup(rubberBand) {
        this.rubberBand = rubberBand;
    }
    
    /**
     * 伸び率から評価を判定
     * @param {number} stretchRatio - 伸び率（0-1の範囲）
     * @returns {string} 評価
     */
    evaluateStretch(stretchRatio) {
        // 各評価の判定
        for (const [evaluation, range] of Object.entries(CONSTANTS.STRETCH_EVALUATION)) {
            if (stretchRatio >= range.min && stretchRatio < range.max) {
                return CONSTANTS.EVALUATION.TEXT[evaluation];
            }
        }
        return CONSTANTS.EVALUATION.TEXT.FAIL;
    }

    /**
     * 評価に応じた得点倍率を計算
     * @param {string} evaluation - 評価
     * @returns {number} 倍率
     */
    getMultiplierForEvaluation(evaluation) {
        // 評価テキストから対応するキーを取得
        const evaluationKey = Object.entries(CONSTANTS.EVALUATION.TEXT)
            .find(([key, value]) => value === evaluation)?.[0];
        
        if (!evaluationKey) {
            console.warn('Unknown evaluation:', evaluation);
            return 0;
        }
        
        if (evaluationKey === 'PERFECT') {
            // 完璧の場合は獲得回数×10（最大200倍）
            return Math.min(this.perfectCount * 10, 200);
        }
        
        // その他の評価は設定された倍率を返す
        return CONSTANTS.STRETCH_EVALUATION[evaluationKey].multiplier;
    }

    /**
     * ターゲット衝突時の得点計算と追加
     * @param {object} targetObj - ターゲットオブジェクト
     * @param {number} comboCount - コンボ数
     * @returns {number} 現在のスコア
     */
    calculateAndAddPoints(targetObj, comboCount) {
        if (!this.rubberBand) {
            console.error('ScoreManager: rubberBand が設定されていません');
            return this.score;
        }
        
        // 伸び率の取得と評価の判定
        const stretchRatio = this.rubberBand.getStretchRatio();
        const evaluation = this.evaluateStretch(stretchRatio);
        
        // 評価カウントの更新
        // 評価テキストからキーを取得
        const evaluationKey = Object.entries(CONSTANTS.EVALUATION.TEXT)
            .find(([key, value]) => value === evaluation)?.[0];
        
        if (evaluationKey) {
            this.evaluationCounts[evaluationKey]++;
            
            if (evaluationKey === 'PERFECT') {
                this.perfectCount++;
            }
        }
        
        // ターゲットの位置を取得
        const targetPosition = targetObj.getPosition();
        
        // 評価による倍率を取得
        const evaluationMultiplier = this.getMultiplierForEvaluation(evaluation);
        
        // 桁数ボーナスの計算（1桁:1倍, 2桁:2倍, 3桁:3倍...）
        const digitBonus = Math.max(1, Math.floor(Math.log10(comboCount) + 1));
        
        // 得点の計算（コンボ数×桁数ボーナス×評価倍率×伸び率）
        const comboMultiplier = comboCount * digitBonus;
        const finalPoints = Math.round(comboMultiplier * evaluationMultiplier * stretchRatio * 100);
        
        // 得点の追加
        this.score += finalPoints;
        
        // スコア表示の更新
        this.updateScoreText();
        
        // 得点獲得エフェクト
        this.createScoreEffect(finalPoints, comboCount > 1, targetPosition, stretchRatio);
        
        return this.score;
    }
    
    /**
     * 得点を直接追加（テスト用や特殊なボーナス用）
     * @param {number} points - 追加する得点
     * @param {boolean} isCombo - コンボによる得点かどうか
     * @param {object} position - 得点エフェクトを表示する位置 {x, y}
     * @returns {number} 現在のスコア
     */
    addPoints(points, isCombo = false, position = null) {
        // 得点の追加
        this.score += points;
        
        // スコア表示の更新
        this.updateScoreText();
        
        // 得点獲得エフェクト
        this.createScoreEffect(points, isCombo, position, 0);
        
        return this.score;
    }
    
    /**
     * スコアを表示用にフォーマット
     * @param {number} score - スコア値
     * @returns {string} フォーマットされたスコア文字列
     */
    formatScore(score) {
        // 単純に数値をカンマ区切りで表示
        return score.toLocaleString();
    }
    
    /**
     * スコアテキストの更新
     */
    updateScoreText() {
        this.scoreValueText.setText(this.formatScore(this.score));
    }
    
    /**
     * 得点獲得エフェクトの作成
     * @param {number} points - 獲得した得点
     * @param {boolean} isCombo - コンボによる得点かどうか
     * @param {object} position - 得点エフェクトを表示する位置 {x, y}
     * @param {number} stretchRatio - 伸び率（0-1の範囲）
     */
    createScoreEffect(points, isCombo = false, position = null, stretchRatio = 0) {
        // 評価の取得
        const evaluation = this.evaluateStretch(stretchRatio);
        
        // 評価に応じた色の設定（評価テキスト用）
        const colors = {
            [CONSTANTS.EVALUATION.TEXT.FAIL]: CONSTANTS.EVALUATION.SETTINGS.FAIL.color,
            [CONSTANTS.EVALUATION.TEXT.PASS]: CONSTANTS.EVALUATION.SETTINGS.PASS.color,
            [CONSTANTS.EVALUATION.TEXT.GOOD]: CONSTANTS.EVALUATION.SETTINGS.GOOD.color,
            [CONSTANTS.EVALUATION.TEXT.GREAT]: CONSTANTS.EVALUATION.SETTINGS.GREAT.color,
            [CONSTANTS.EVALUATION.TEXT.PERFECT]: CONSTANTS.EVALUATION.SETTINGS.PERFECT.color
        };

        let x, y;
        
        // 位置が指定されていない場合はスコア表示の横に表示
        if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) {
            x = this.scoreValueText.x + this.scoreValueText.width + 20;
            y = this.scoreValueText.y;
        } else {
            // ターゲットの位置を基準に表示
            x = position.x;
            y = position.y;
        }
        
        // 得点表示エフェクト用のコンテナを作成
        const container = this.scene.add.container(x, y);
        container.setDepth(20);
        
        // 評価に応じたスタイル設定
        const styles = {
            [CONSTANTS.EVALUATION.TEXT.FAIL]: {
                fontSize: '32px',
                scale: 1.0,
                particleCount: 0
            },
            [CONSTANTS.EVALUATION.TEXT.PASS]: {
                fontSize: '40px',
                scale: 1.2,
                particleCount: 10
            },
            [CONSTANTS.EVALUATION.TEXT.GOOD]: {
                fontSize: '48px',
                scale: 1.4,
                particleCount: 20
            },
            [CONSTANTS.EVALUATION.TEXT.GREAT]: {
                fontSize: '56px',
                scale: 1.6,
                particleCount: 30
            },
            [CONSTANTS.EVALUATION.TEXT.PERFECT]: {
                fontSize: '64px',
                scale: 1.8,
                particleCount: 50
            }
        };

        const style = styles[evaluation];

        // 評価テクスチャ（最上部）
        const evaluationKey = Object.entries(CONSTANTS.EVALUATION.TEXT)
            .find(([key, value]) => value === evaluation)?.[0];
        const textureName = CONSTANTS.EVALUATION.TEXTURE_KEYS[evaluationKey];

        const evaluationTexture = this.scene.add.image(
            0,
            -style.fontSize.replace('px', '') * 1.5,
            textureName
        );
        evaluationTexture.setOrigin(0.5);
        evaluationTexture.setScale(style.scale * 0.5); // テクスチャサイズに合わせて調整

        // 得点表示テキスト（中央）
        const pointsText = this.scene.add.text(
            0,
            evaluationTexture.height * evaluationTexture.scale * 0.5,
            '+' + points,
            { 
                fontFamily: 'DotGothic16',
                fontSize: '32px',
                fill: '#ffa500', // オレンジ
                alpha: 0.8, // より控えめに
                stroke: '#000',
                strokeThickness: 3
            }
        );
        pointsText.setOrigin(0.5);
        
        // 伸び率表示テキスト（下部）
        const stretchText = this.scene.add.text(
            0,
            evaluationTexture.height * evaluationTexture.scale * 0.5 + pointsText.height + 5,
            Math.round(stretchRatio * 100) + '%',
            {
                fontFamily: 'DotGothic16',
                fontSize: '24px',
                fill: '#aaaaaa', // グレー
                stroke: '#000',
                strokeThickness: 2,
                alpha: 0.7 // より控えめに
            }
        );
        stretchText.setOrigin(0.5);

        // パーティクルエフェクト（優良以上の評価の場合）
        if (evaluation !== CONSTANTS.EVALUATION.TEXT.FAIL && evaluation !== CONSTANTS.EVALUATION.TEXT.PASS) {
            for (let i = 0; i < style.particleCount; i++) {
                const particle = this.scene.add.circle(0, 0, 3, parseInt(colors[evaluation].slice(1), 16));
                const angle = Math.random() * Math.PI * 2;
                const speed = Math.random() * 2 + 2;
                particle.dx = Math.cos(angle) * speed;
                particle.dy = Math.sin(angle) * speed;
                
                this.scene.tweens.add({
                    targets: particle,
                    alpha: 0,
                    scale: 0.1,
                    duration: 1000,
                    onComplete: () => particle.destroy(),
                    onUpdate: () => {
                        particle.x += particle.dx;
                        particle.y += particle.dy;
                        particle.dy += 0.1; // 重力効果
                    }
                });
                
                container.add(particle);
            }
        }
        
        // コンテナに追加
        container.add([pointsText, evaluationTexture, stretchText]);

        // 評価テクスチャのアニメーション
        this.scene.tweens.add({
            targets: evaluationTexture,
            scale: { from: style.scale * 0.75, to: style.scale * 0.5 },
            duration: 500,
            ease: 'Back.easeOut'
        });

        // 得点テキストのアニメーション
        this.scene.tweens.add({
            targets: pointsText,
            scale: { from: 1.2, to: 1 },
            alpha: { from: 0.8, to: 0.8 }, // アニメーションなし、透明度固定
            duration: 100
        });

        // コンテナの位置を調整
        this.adjustContainerPosition(container);
        
        // 評価テクスチャのフェードアウト
        this.scene.tweens.add({
            targets: evaluationTexture,
            y: evaluationTexture.y - 50,
            alpha: 0,
            duration: 2000,
            ease: 'Power2'
        });

        // 得点と伸び率のフェードアウト
        this.scene.tweens.add({
            targets: [pointsText, stretchText],
            y: '-=20',
            alpha: 0,
            duration: 2000,
            ease: 'Linear',
            onComplete: () => {
                container.destroy();
            }
        });
    }
    
    /**
     * コンテナが画面からはみ出ないように位置を調整
     * @param {Phaser.GameObjects.Container} container - 調整するコンテナ
     */
    adjustContainerPosition(container) {
        const padding = 10; // 画面端からの余白
        const gameWidth = this.scene.game.config.width;
        const gameHeight = this.scene.game.config.height;
        
        // コンテナの幅と高さを計算
        const bounds = container.getBounds();
        const halfWidth = bounds.width * 0.5;
        const halfHeight = bounds.height * 0.5;
        
        // X座標の調整
        if (container.x - halfWidth < padding) {
            container.x = padding + halfWidth;
        } else if (container.x + halfWidth > gameWidth - padding) {
            container.x = gameWidth - padding - halfWidth;
        }
        
        // Y座標の調整
        if (container.y - halfHeight < padding) {
            container.y = padding + halfHeight;
        } else if (container.y + halfHeight > gameHeight - padding) {
            container.y = gameHeight - padding - halfHeight;
        }
    }
    
    getScore() {
        return this.score;
    }
    
    getHighScore() {
        return this.highScore;
    }
    
    updateHighScore() {
        // ハイスコアの更新
        if (this.score > this.highScore) {
            this.highScore = this.score;
            this.saveHighScore();
            return true; // 更新された
        }
        return false; // 更新されなかった
    }
    
    reset() {
        // スコアのリセット
        this.score = 0;
        this.updateScoreText();
        
        // 評価カウントのリセット
        // 評価カウントをキーベースでリセット
        Object.keys(this.evaluationCounts).forEach(key => {
            this.evaluationCounts[key] = 0;
        });
        this.perfectCount = 0;
    }
    
    loadHighScore() {
        // ローカルストレージからハイスコアを読み込む
        try {
            const storedHighScore = localStorage.getItem('rubberBandGameHighScore');
            if (storedHighScore !== null) {
                const parsedScore = parseInt(storedHighScore, 10);
                if (!isNaN(parsedScore)) {
                    this.highScore = parsedScore;
                }
            }
        } catch (error) {
            console.error('Error loading high score:', error);
        }
    }
    
    /**
     * 評価カウントを取得
     * @returns {Object} 評価カウント
     */
    getEvaluationCounts() {
        return { ...this.evaluationCounts };
    }

    saveHighScore() {
        // ローカルストレージにハイスコアを保存
        try {
            localStorage.setItem('rubberBandGameHighScore', this.highScore.toString());
            
            // 評価カウントも保存
            localStorage.setItem('rubberBandGameEvaluationCounts', JSON.stringify(this.evaluationCounts));
        } catch (error) {
            console.error('Error saving high score:', error);
        }
    }
}
